<template>
    <!-- Upgrade in progress: results are being recomputed against the new panel -->
    <div v-if="updateInProgress" class="alert alert-info d-flex gap-2 align-items-start" role="status">
        <div class="upgrade-spinner mt-1" aria-hidden="true" />
        <div class="text-start">
            <p class="fs-14 fw-semibold mb-1">We're building your upgraded ancestry breakdown</p>
            <p class="fs-14 mb-0">
                Your DNA is being reanalysed against our latest reference panel, including the new
                Irish regional breakdown. Your updated results and map will appear here automatically
                once they're ready — this can take a little while.
            </p>
        </div>
    </div>

    <!-- Upgrade available: prompt the user to opt in -->
    <div v-else-if="updateAvailable" class="alert alert-info text-start" role="alert">
        <p class="fs-14 fw-semibold mb-1">
            <feather-wrapper icon="map" class="mr-2" />
            A panel upgrade is available
        </p>
        <p class="fs-14 mb-2">
            We've expanded our reference panel with a new Irish regional breakdown. Opt in to
            reanalyse your DNA and unlock more detail in your recent ancestry results and map.
        </p>
        <p v-if="errorMessage" class="fs-14 text-danger mb-2">{{ errorMessage }}</p>
        <button
            type="button"
            class="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
            :disabled="submitting"
            @click="optIn"
        >
            <feather-wrapper icon="zap" />
            {{ submitting ? "Starting upgrade…" : "Upgrade my results" }}
        </button>
    </div>
</template>

<script setup>
import { ref, computed } from "vue";
import store from "@/store";

const submitting = ref(false);
const errorMessage = ref("");

const updateAvailable = computed(() => store.getters.autosomalUpdateAvailable);
const updateInProgress = computed(() => store.getters.autosomalUpdateInProgress);
const barcode = computed(() => store.getters.profile.profile.barcode);

async function optIn() {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    errorMessage.value = "";
    try {
        // Reuses the existing autosomal update flow: persists the choice and triggers the
        // reprocess job on the backend, then flips the kit test into the "updating" state.
        await store.dispatch("upgradeTest", { barcode: barcode.value, test: "autosomal" });
    } catch (e) {
        console.error("Failed to start panel upgrade", e);
        errorMessage.value = "Sorry, we couldn't start your upgrade. Please try again.";
    } finally {
        submitting.value = false;
    }
}
</script>

<style scoped lang="scss">
.upgrade-spinner {
    width: 18px;
    height: 18px;
    border: 3px solid #3b82f6;
    border-top-color: transparent;
    border-radius: 50%;
    animation: upgrade-spin 1s linear infinite;
    flex: 0 0 auto;
}

@keyframes upgrade-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
